import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../../globals';
import { LabeledCheckbox } from '../../../generic';
import Flex from '../../../generic/Flex';
import Section from '../../../shared/settings/SettingsPageSection';
import NumberInputWithUnit from '../../shared/NumberInputWithUnit';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import type { ProjectSnippetSettings } from '../../../../../shared/settings/project/snippets';
import type { OrganizationSettingsEndpoint } from '../../shared/useOrganizationSettings';

import PropagateSnippetSettingsModal from './PropagateSnippetSettingsModal';

import styles from './SnippetsSection.module.less';

const DEFAULT_THRESHOLD = 10;

/**
 * Displays the snippets section of the project general settings page.
 */
const SnippetsSection: VoidFunctionComponent = () => {
  const [isPropagateSnippetsModalOpen, setIsPropagateSnippetsModalOpen] = useState<boolean>(false);
  const endpoint = useMemo<OrganizationSettingsEndpoint>(
    () => `/api/organizations/${window.user?.organizationId ?? 0}/settings/projects/snippets`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [window.user?.organizationId],
  );
  const { isDirty, onUpdateSettings, settings, isLoading, onSaveSettings, isSaveButtonDisabled } =
    useOrganizationSettings<ProjectSnippetSettings>({
      endpoint,
      settingsSection: 'snippets',
    });

  const threshold = settings?.projectDefaultSnippetAutoRejectMatchPercentageThreshold ?? undefined;
  const isEnabled = threshold !== undefined;
  return (
    <>
      <Section>
        <Section.Left heading="Snippets">
          <Section.Paragraph>
            Controls snippet detection behavior in all projects.
          </Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton isDisabled={isDirty} onClick={() => setIsPropagateSnippetsModalOpen(true)} />
            <Section.SaveButton onClick={onSaveSettings} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <Flex direction="column" align="flex-start" gap="16px">
            <Section.Heading>Auto Rejection</Section.Heading>
            <Flex gap="12px" align="center" justify="flex-start">
              <div className={styles.checkbox}>
                <LabeledCheckbox
                  label="Enabled"
                  isChecked={isEnabled}
                  onChange={(_, checked) => {
                    onUpdateSettings({
                      projectDefaultSnippetAutoRejectMatchPercentageThreshold: checked ? DEFAULT_THRESHOLD : null,
                    });
                  }}
                />
              </div>
              {isEnabled && (
                <NumberInputWithUnit
                  unit="percent"
                  label="SMatch Percentage Threshold"
                  value={threshold}
                  onChange={(newThreshold) => onUpdateSettings({
                    projectDefaultSnippetAutoRejectMatchPercentageThreshold: newThreshold ?? null,
                  })}
                  isDisabled={!isEnabled || isLoading.any}
                />
              )}
            </Flex>
            <div className={`${styles.smallText} ${styles.smallTextInfo}`}>
              Any snippets files with a match percentage less than or equal to this value will be
              automatically rejected. They will still appear in the UI as a rejected snippet.
            </div>
          </Flex>
        </Section.Right>
      </Section>
      {settings && (
        <PropagateSnippetSettingsModal
          isOpen={isPropagateSnippetsModalOpen}
          settings={settings}
          onHide={() => setIsPropagateSnippetsModalOpen(false)}
          endpoint={endpoint}
        />
      )}
    </>
  );
};

export default SnippetsSection;
